import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Target, CheckCircle, AlertTriangle, Printer, Loader2, MinusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import EvaluationRequestPanel from '@/components/EvaluationRequestPanel';

const scoreColor = (s) => (s >= 70 ? '#16A34A' : s >= 50 ? '#F59E0B' : '#DC2626');

const ScoreGauge = ({ score }) => (
  <div className="flex flex-col items-center" data-testid="auto-eval-score-gauge">
    <div className="relative w-36 h-36">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="#E5E7EB" strokeWidth="12" />
        <circle
          cx="60" cy="60" r="52" fill="none"
          stroke={scoreColor(score)} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={`${(score / 100) * 326.7} 326.7`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold" style={{ color: scoreColor(score) }}>{score}</span>
        <span className="text-xs text-gray-500">/ 100</span>
      </div>
    </div>
  </div>
);

const CriterionCard = ({ criterion, result }) => {
  const applicable = result && result.applicable !== false;
  const score = applicable ? Math.max(0, Math.min(100, Number(result.score) || 0)) : null;
  return (
    <div
      className={`p-4 rounded-xl border ${applicable ? 'bg-white' : 'bg-gray-50 opacity-70'}`}
      data-testid={`criterion-${criterion.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm" style={{ color: '#002768' }}>{criterion.label}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">{criterion.weight} %</span>
        </div>
        {applicable ? (
          <span className="text-lg font-bold" style={{ color: scoreColor(score) }}>{score}</span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-gray-500"><MinusCircle className="w-3.5 h-3.5" /> Non applicable</span>
        )}
      </div>
      {applicable && (
        <div className="h-2 rounded-full bg-gray-100 mb-2">
          <div className="h-2 rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: scoreColor(score) }} />
        </div>
      )}
      {result?.justification && <p className="text-xs text-gray-600 leading-relaxed">{result.justification}</p>}
    </div>
  );
};

export default function AutoEvaluation({ studentInfo, isAdmin }) {
  const { sessionType, sessionId } = useParams();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  const generateEvaluation = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await axios.post(`${API}/auto-evaluation/${sessionType}/${sessionId}?admin=true`);
      setEvaluation(resp.data);
      setNotFound(false);
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur lors de la génération de l'évaluation";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [sessionType, sessionId]);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${API}/auto-evaluation/${sessionType}/${sessionId}`);
        setEvaluation(resp.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("Erreur lors du chargement de l'évaluation");
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionType, sessionId]);

  if (loading || generating) {
    return (
      <div className="min-h-screen pt-24 px-4 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }}>
        <Loader2 className="w-10 h-10 animate-spin mb-4" style={{ color: '#002768' }} />
        <p className="text-gray-700 font-medium" data-testid="auto-eval-loading">
          {generating ? "Analyse de votre session par l'IA en cours…" : 'Chargement…'}
        </p>
        {generating && <p className="text-xs text-gray-500 mt-1">Cela peut prendre 15 à 30 secondes.</p>}
      </div>
    );
  }

  if (notFound && !evaluation) {
    return (
      <div className="min-h-screen pt-24 px-4" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }}>
        <div className="max-w-lg mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4" data-testid="auto-eval-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="text-center mb-5">
            <Target className="w-10 h-10 mx-auto mb-2" style={{ color: '#002768' }} />
            <h1 className="text-xl font-bold" style={{ color: '#002768' }}>Évaluation automatique</h1>
            <p className="text-sm text-gray-600">Aucune évaluation n'existe encore pour cette session.</p>
          </div>
          <EvaluationRequestPanel
            requestType="auto_evaluation"
            sessionType={sessionType}
            sessionId={sessionId}
            studentInfo={studentInfo}
            isAdmin={isAdmin}
            onAdminGenerate={generateEvaluation}
            generating={generating}
          />
        </div>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="min-h-screen pt-24 px-4 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }}>
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <p className="text-gray-700 mb-4" data-testid="auto-eval-error">{error || 'Évaluation introuvable'}</p>
        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const data = evaluation.data || {};
  const grid = evaluation.grid_used || [];
  const resultMap = {};
  (data.criteres || []).forEach(r => { resultMap[r.id] = r; });

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8 pb-12 print:pt-4" style={{ background: 'linear-gradient(135deg, #f8fbff 0%, #CAE9FA 40%, #f0fdf4 100%)' }}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between print:hidden">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900" data-testid="auto-eval-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#002768' }} data-testid="auto-eval-print-btn">
            <Printer className="w-4 h-4" /> Imprimer
          </button>
        </div>

        <Card className="glass border-0">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <ScoreGauge score={evaluation.score_global} />
              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <Target className="w-5 h-5" style={{ color: '#002768' }} />
                  <h1 className="text-xl font-bold" style={{ color: '#002768' }}>Évaluation automatique</h1>
                </div>
                <p className="text-sm text-gray-700 font-medium" data-testid="auto-eval-target-title">{evaluation.target_title}</p>
                <p className="text-xs text-gray-500">
                  {evaluation.student_name} • {new Date(evaluation.created_at).toLocaleString('fr-CA')}
                  {evaluation.grid_source === 'override' && ' • Grille personnalisée'}
                </p>
                {data.commentaire_general && (
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed italic" data-testid="auto-eval-general-comment">« {data.commentaire_general} »</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div>
          <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Détail par critère</h2>
          <div className="space-y-3">
            {grid.map(c => <CriterionCard key={c.id} criterion={c} result={resultMap[c.id]} />)}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {(data.points_forts || []).length > 0 && (
            <Card className="glass border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-green-700"><CheckCircle className="w-4 h-4" /> Points forts</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5" data-testid="auto-eval-strengths">
                  {data.points_forts.map((p, i) => <li key={i} className="text-xs text-gray-700 flex gap-2"><span className="text-green-600">✓</span>{p}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}
          {(data.points_ameliorer || []).length > 0 && (
            <Card className="glass border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-amber-700"><AlertTriangle className="w-4 h-4" /> Points à améliorer</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5" data-testid="auto-eval-improvements">
                  {data.points_ameliorer.map((p, i) => <li key={i} className="text-xs text-gray-700 flex gap-2"><span className="text-amber-600">→</span>{p}</li>)}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
